import { basicAuthHeader, readCredentials } from '@/auth/storage';
import { getApiWorkspace } from '@/api/client';

const baseURL = import.meta.env.VITE_API_BASE_URL as string;

export interface SseEvent {
  event: string;
  data: string;
}

export interface PostChatMessageStreamInput {
  sessionId: string;
  message: string;
  expectedLastMessageId?: string | null;
  onEvent: (event: SseEvent) => void;
  signal?: AbortSignal;
}

export async function postChatMessageStream(input: PostChatMessageStreamInput): Promise<void> {
  const creds = readCredentials();
  if (!creds) throw new Error('Not authenticated');

  const ws = getApiWorkspace();
  const res = await fetch(
    `${baseURL}/v1/bot/chat/sessions/${encodeURIComponent(input.sessionId)}/messages`,
    {
      method: 'POST',
      headers: {
        Authorization: basicAuthHeader(creds),
        ...(ws.orgId ? { 'X-Org-Id': ws.orgId } : {}),
        ...(ws.botId ? { 'X-Bot-Id': ws.botId } : {}),
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        message: input.message,
        ...(input.expectedLastMessageId !== undefined
          ? { expectedLastMessageId: input.expectedLastMessageId }
          : {}),
      }),
      ...(input.signal ? { signal: input.signal } : {}),
    },
  );

  if (!res.ok || !res.body) {
    let detail = '';
    try {
      detail = await res.text();
    } catch {}
    throw new Error(detail || `HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep = buffer.indexOf('\n\n');
      while (sep !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseEventBlock(block);
        if (parsed) input.onEvent(parsed);
        sep = buffer.indexOf('\n\n');
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {}
  }
}

function parseEventBlock(block: string): SseEvent | null {
  const lines = block.split('\n');
  let eventName = 'message';
  const dataLines: string[] = [];
  let hasData = false;

  for (const line of lines) {
    if (line.length === 0) continue;
    if (line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const valueRaw = colon === -1 ? '' : line.slice(colon + 1);
    const value = valueRaw.startsWith(' ') ? valueRaw.slice(1) : valueRaw;
    if (field === 'event') {
      eventName = value;
    } else if (field === 'data') {
      dataLines.push(value);
      hasData = true;
    }
  }

  if (!hasData) return null;
  return { event: eventName, data: dataLines.join('\n') };
}
